import React from 'react';

import clsx from 'clsx';

import {
	AGENCY_PROJECT,
	PHOTOS_BASE_PATH,
	PROJECT_CAR,
	SUB_AGENCY_PROJECT,
	PROJECT_CCG,
	PROJECT_CGL,
	PROJECT_CVC,
	PROJECT_CCC,
	PROJECT_CGR,
	PROJECT_SOS,
	PROJECT_DOG,
	CURRENT_CRI_LIST,
	PROJECT_CSV,
	CTA_CVC,
	SITE_CVC,
	SOS_PHONE,
	DOG_SITE,
	PIN_PROP,
	FILIALI_PROJECT,
	PROJECT_CAR2,
	CURRENT_GRA_LIST,
	CAR_TYPES,
	CAR_CERTIFICATIONS,
	GRA_LOGOS
} from '../utils/Constants';

import Services from '../components/Services';
import ResultIcons from '../components/ResultIcons';
import Button from '../components/Button';
import TimeTable from '../components/TimeTable';
import Label from '../components/Label';
import Brand from '../components/Brand';
import Icon from '../components/Icon';

import infoWindowStyles from '../components/InfoWindow.module.scss';
import iconStyles from '../components/Icon.module.scss';

import { useOpenWebForm } from './geocms';
import { useTCLog } from '../utils/TCEvent';
import { useInputParams } from '../utils/InputParamsProvider';
import {
	getResultId,
	getCompleteAddress,
	getCarType,
	csvTypeConverter
} from './geocmsUtil';

/*
Hook pensato per ridurre tutti i risultati provenienti dalle varie fonti
a degli oggetti simili fra loro. Aggiungere una nuova funzione di normalizzazione
quando si aggiunge una nuova fonte dati.
*/
export function useNormalResult() {
	const [TCLog] = useTCLog();
	const [RANKING, ,] = useInputParams();
	const openWebForm = useOpenWebForm();

	function normalizeBaseResult(
		id,
		project,
		name,
		lat,
		lng,
		pin,
		address,
		distance,
		cta,
		phone,
		fax,
		email,
		site,
		facebook,
		brand,
		timeTable
	) {
		return {
			id: id,
			project: project,
			name: name,
			lat: lat,
			lng: lng,
			pin: pin,
			address: address,
			distance: distance,
			cta: cta,
			phone: phone,
			fax: fax,
			email: email,
			site: site,
			facebook: facebook,
			brand: brand,
			timeTable: timeTable
		};
	}

	function normalizeGeoCMSBaseResult(
		project,
		result,
		name,
		cta,
		phone,
		fax,
		email,
		site,
		facebook,
		brand,
		detail
	) {
		const geoElement = result.getGeoElements
			? result.getGeoElements()[0]
			: result.getGeoElement();

		const resultPosition = geoElement?.getOverlay().getPosition();

		const lat = resultPosition?.lat();
		const lng = resultPosition?.lng();

		const data = result.getUserdata();

		const distance = result.driveDistance
			? result.driveDistance
			: project === PROJECT_CCG
			? parseFloat(data['distance']) * 1000
			: data['GeoCMS_Distance'];

		return {
			id: getResultId(result),
			project: project,
			name: data[name],
			lat: lat,
			lng: lng,
			address: getCompleteAddress(data, project),
			distance: parseFloat(distance),
			cta: cta,
			phone: data[phone],
			fax: data[fax],
			email: data[email],
			site: data[site],
			facebook: data[facebook],
			brand: brand,
			timeTable: getTimeTableProps(data, project === PROJECT_CAR),
			detail: detail
		};
	}

	function normalizeGeoCMSResults(results) {
		const resultsArray = [];

		for (let i = 0; i < getLength(results); i++) {
			const result = getListElementAt(results, i);

			let normalizedResult = normalizeGeoCMSResult(result);

			if (normalizedResult) resultsArray.push(normalizedResult);
		}

		return resultsArray;
	}

	function normalizeGeoCMSResult(result) {
		const project = result.idProject;

		if (project === AGENCY_PROJECT || project === SUB_AGENCY_PROJECT) {
			return normalizeAgencyResult(result);
		} else if (project === FILIALI_PROJECT) {
			return normalizeMPSResult(result);
		} else if (project === PROJECT_CAR) {
			return normalizeCarResult(result);
		} else if (CURRENT_CRI_LIST.includes(project)) {
			return normalizeCriResult(result);
		} else if (CURRENT_GRA_LIST.includes(project)) {
			return normalizeGraResult(result);
		} else if (project === PROJECT_CSV) {
			return normalizeCsvResult(result);
		} else return null;
	}

	function normalizeAgencyResult(result) {
		const data = result.getUserdata();

		const isSubAgency = data['IDSub'] ? true : false;
		const isSecondarySite = data['Tipo sede'] === 'S';

		let img = data['social.owner.photo']
			? PHOTOS_BASE_PATH + data['social.owner.photo']
			: null;

		if (!img) {
			img = require('../images/icon/agenzia_filiali_placeholder.png');
		}

		if (data['IDSub']) {
			img = require('../images/icon/subagenzia_filiali_placeholder.png');
		}

		const normalizedResult = normalizeGeoCMSBaseResult(
			AGENCY_PROJECT,
			result,
			'Intestatario mandato',
			data['IDSub'] ? null : 'Fissa Appuntamento',
			'Telefono 1',
			'Fax',
			'Email_Istituzionale_1',
			'Sito_Istituzionale',
			'Facebook_Istituzionale',
			<Brand img={img} />
		);

		normalizedResult.city = data['Nome comune'];
		normalizedResult.cap = data['CAP'];
		normalizedResult.prov = data['Provincia'];

		normalizedResult.pin = isSubAgency
			? PIN_PROP.IMGs[SUB_AGENCY_PROJECT]
			: PIN_PROP.IMGs[AGENCY_PROJECT];

		normalizedResult.rank = result.getUserdataGroupValue(
			RANKING ? RANKING : 'QUOTATORE AUTO',
			'ranking',
			0
		);

		normalizedResult.isRankedAgency =
			normalizedResult.rank && normalizedResult.rank.trim() != '';

		normalizedResult.agencyId = data['IDAgenzia'];

		if (isSecondarySite) {
			normalizedResult.siteId = data['IDSede'];
		}

		if (isSubAgency) {
			normalizedResult.subAgencyId = data['IDSub'];
		}

		window['cta-' + normalizedResult.id] = () => {
			TCLog(eventName);
			openWebForm(normalizedResult);
		};

		const eventName =
			(RANKING ? 'PP_' : '') +
			(isSubAgency
				? 'FISSA_APP_SUBAGENZIA'
				: 'RICHIEDI_APPUNTAMENTO_QUI');

		const agencyButton = normalizedResult.isRankedAgency ? (
			<Button
				variant="special"
				className={clsx(infoWindowStyles.button, 'cta')}
				text={normalizedResult.cta}
				onClick={() => {
					TCLog(eventName);
					openWebForm(normalizedResult);
				}}
			/>
		) : null;

		normalizedResult.thirdColumnList = normalizedResult.timeTable ? (
			<TimeTable data={normalizedResult.timeTable} />
		) : null;
		normalizedResult.fourthColumnList = isSubAgency ? null : agencyButton;
		normalizedResult.infoWindowFooter = isSubAgency ? null : agencyButton;

		return normalizedResult;
	}

	function normalizeMPSResult(result) {
		const data = result.getUserdata();

		let img = require('../images/mps_logo.png');

		const normalizedResult = normalizeGeoCMSBaseResult(
			FILIALI_PROJECT,
			result,
			'descrizioneFiliale',
			'Calcola percorso',
			'telefono',
			'fax',
			null,
			null,
			null,
			<Brand img={img} />
		);

		normalizedResult.pin = PIN_PROP.IMGs[FILIALI_PROJECT];

		const mpsButton = (
			<Button
				variant="special"
				className={clsx(infoWindowStyles.button, 'cta')}
				text={normalizedResult.cta}
			/>
		);

		normalizedResult.fourthColumnList = mpsButton;

		normalizedResult.infoWindowFooter = mpsButton;

		return normalizedResult;
	}

	function normalizeCarResult(result) {
		const data = result.getUserdata();
		const type = getCarType(data);

		let img = '';

		if (type === CAR_TYPES.METAFORA) {
			img = require('../images/metafora_logo.png');
		} else if (type === CAR_TYPES.CARCLINIC) {
			img = require('../images/carclinic.png');
		} else if (type === CAR_TYPES.SOLUZIONE_CARROZZERIE) {
			img = require('../images/carsafe_logo.svg');
		} else if (type === CAR_TYPES.AUTOSICURA) {
			img = require('../images/autosicura.png');
		} else if (type === CAR_TYPES.RIPARTE) {
			img = require('../images/riparte.png');
		} else {
			img = require('../images/carrozzeria_axa.png');
		}
		// evolutiva BMW
		if (data['BMW MINI'] === 'SI') {
            img = require('../images/services/logo_bmw_mini.png'); 
        }

		const normalizedResult = normalizeGeoCMSBaseResult(
			PROJECT_CAR,
			result,
			'NomeCarrozzeria',
			'Servizi e contatti',
			'telefono',
			'fax',
			'email',
			null,
			null,
			// <Brand variant={type} content={typeLabel} />
			//<Brand img={img} />
			<Brand img={img.default || img} content={type || 'Carrozzeria'} />
		);

		normalizedResult.geoCMSData = data;

		if (type === CAR_TYPES.CARCLINIC) {
			normalizedResult.pin = PIN_PROP.IMGs[type + '_Carrozzerie'];
		} else {
			normalizedResult.pin = PIN_PROP.IMGs[type];
		}

		normalizedResult.fourthColumnList = normalizedResult.timeTable ? (
			<TimeTable data={normalizedResult.timeTable} />
		) : null;

		normalizedResult.thirdColumnList = <Services data={data} />;

		normalizedResult.ResultIcons = <ResultIcons data={data} />;

		normalizedResult.additionalInfo = CAR_CERTIFICATIONS.map(
			(carCertification) => {
				if (data[carCertification.name]) {
					return (
						<>
							<Label
								text={<span>{carCertification.text}</span>}
								icon={
									<Icon
										icon={carCertification.icon}
										size="small"
										textOn="right"
										className={iconStyles.alignSelfCenter}
									/>
								}
								variant="flexSmall"
								noUppercase={true}
							/>
						</>
					);
				}
			}
		);

		let isBookingAvailable = false;
		let bookingFnc;

		if (
			typeof window.Liferay === 'object' &&
			window.Liferay.ThemeDisplay.isSignedIn()
		) {
			isBookingAvailable = true;
			bookingFnc = () => {
				if (typeof window.showBookingForm === 'function') {
					const jsonData = {
						id: normalizedResult.id,
						name: normalizedResult.name,
						phone: normalizedResult.phone,
						site: normalizedResult.site,
						address: normalizedResult.address
					};
					window.showBookingForm(jsonData);
				} else {
					console.error('Function showBookingForm not found.');
				}
			};
		}

		normalizedResult.infoWindowFooter = (
			<>
				{isBookingAvailable ? (
					<Button
						variant="special"
						className={clsx(infoWindowStyles.button, 'cta')}
						text={normalizedResult.cta}
						onClick={() => bookingFnc()}
					/>
				) : null}

				<Label text="Servizi al cliente" />
				<Services data={data} />
			</>
		);

		return normalizedResult;
	}

	function normalizeCriResult(result) {
		const project = result['idProject'];
		let normalizedResult;

		normalizedResult =
			project === PROJECT_CCG
				? normalizeCcgResult(result)
				: project === PROJECT_CGL
				? normalizeCglResult(result)
				: project === PROJECT_CVC
				? normalizeCvcResult(result)
				: null;

		if (normalizedResult) {
			normalizedResult.pin = PIN_PROP.IMGs[normalizedResult.project];
		}

		return normalizedResult;
	}

	function normalizeGraResult(result) {
		const project = result['idProject'];
		let normalizedResult;

		normalizedResult =
			project === PROJECT_CGR
				? normalizeCgrResult(result)
				: project === PROJECT_DOG
				? normalizeDogResult(result)
				: genericNormalizeGrandineResult(result, project);

		if (normalizedResult) {
			normalizedResult.pin = PIN_PROP.IMGs[normalizedResult.project];
		}

		return normalizedResult;
	}

	function normalizeCcgResult(result) {
		const img = require('../images/carglass_logo.png');

		const data = result.getUserdata();

		const normalizedResult = normalizeGeoCMSBaseResult(
			PROJECT_CCG,
			result,
			'name',
			'Prenota online',
			'telefono',
			null,
			'e-mail',
			'iDeal',
			null,
			<Brand img={img} />
		);

		const ccgButton = (
			<Button
				variant="special"
				className={infoWindowStyles.button}
				text={normalizedResult.cta}
				link={normalizedResult.site}
			/>
		);

		normalizedResult.thirdColumnList = (
			<strong>Vuoi essere richiamato?</strong>
		);

		normalizedResult.fourthColumnList = ccgButton;

		normalizedResult.infoWindowFooter = (
			<div className={infoWindowStyles.footer}>
				<u>
					<Label
						text="Vuoi essere richiamato?"
						link={data['contactPage']?.replace(/&amp;/g, '&')}
					/>
				</u>
				<p className={infoWindowStyles.footerSummary}>
					Carglass risponderà entro 1 ora.<br></br>
					Servizio attivo lun-ven 8-19.30 e sab 8-17.30
				</p>
				<div>{ccgButton}</div>
			</div>
		);

		return normalizedResult;
	}

	function normalizeCglResult(result) {
		const img = require('../images/glassdrive_logo.jpg');

		const normalizedResult = normalizeGeoCMSBaseResult(
			PROJECT_CGL,
			result,
			'Nome Centro',
			'Contatta via mail',
			'Telefono',
			null,
			'Indirizzo E-mail',
			null,
			null,
			<Brand img={img} />
		);

		const cglButton = (
			<Button
				variant="special"
				className={infoWindowStyles.button}
				text={normalizedResult.cta}
				link={`mailto:${normalizedResult.email}`}
			/>
		);

		normalizedResult.fourthColumnList = cglButton;

		normalizedResult.infoWindowFooter = cglButton;

		return normalizedResult;
	}

	function normalizeCvcResult(result) {
		const img = require('../images/vetrocar_logo.jpg');

		const normalizedResult = normalizeGeoCMSBaseResult(
			PROJECT_CVC,
			result,
			'Nome Centro',
			'Prenota Online',
			'Telefono',
			null,
			'Email',
			null,
			null,
			<Brand img={img} />
		);

		const cvcButton = (
			<Button
				variant="special"
				className={infoWindowStyles.button}
				text={normalizedResult.cta}
				link={CTA_CVC}
			/>
		);

		normalizedResult.fourthColumnList = cvcButton;

		normalizedResult.infoWindowFooter = (
			<div className={infoWindowStyles.footer}>
				<u>
					<Label text="Vuoi essere richiamato?" link={SITE_CVC} />
				</u>
				<div>{cvcButton}</div>
			</div>
		);

		return normalizedResult;
	}

	function normalizeCgrResult(result) {
		const img = require('../images/maestridellagrandine_logo.png');

		const normalizedResult = normalizeGeoCMSBaseResult(
			PROJECT_CGR,
			result,
			'DENOMINAZIONE',
			'Calcola percorso',
			'TELEFONO',
			'FAX',
			null,
			null,
			null,
			<Brand img={img} />
		);

		const cgrButton = (
			<Button
				variant="special"
				className={clsx(infoWindowStyles.button, 'cta')}
				text={normalizedResult.cta}
			/>
		);

		normalizedResult.fourthColumnList = cgrButton;

		normalizedResult.infoWindowFooter = cgrButton;

		return normalizedResult;
	}

	function normalizeSosResult(result) {
		const img = require('../images/sosgrandine_logo.jpg');

		const normalizedResult = normalizeGeoCMSBaseResult(
			PROJECT_SOS,
			result,
			'Anagrafica',
			'Calcola percorso',
			null,
			null,
			null,
			null,
			null,
			<Brand img={img} />
		);

		normalizedResult.phone = SOS_PHONE;

		const sosButton = (
			<Button
				variant="special"
				className={clsx(infoWindowStyles.button, 'cta')}
				text={normalizedResult.cta}
			/>
		);

		normalizedResult.fourthColumnList = sosButton;

		normalizedResult.infoWindowFooter = sosButton;

		return normalizedResult;
	}

	function normalizeDogResult(result) {
		const img = require('../images/dottorgrandine_logo.jpg');

		const normalizedResult = normalizeGeoCMSBaseResult(
			PROJECT_DOG,
			result,
			'Ragione Sociale',
			'Vai al sito',
			'NUMERO DI TELEFONO',
			null,
			'INDIRIZZO MAIL',
			null,
			null,
			<Brand img={img} />
		);

		normalizedResult.site = DOG_SITE;

		const dogButton = (
			<Button
				variant="special"
				className={infoWindowStyles.button}
				text={normalizedResult.cta}
				link={normalizedResult.site}
			/>
		);

		normalizedResult.fourthColumnList = dogButton;

		normalizedResult.infoWindowFooter = (
			<div className={infoWindowStyles.footer}>
				<Label
					variant="small"
					text="Servizio attivo 24/7"
					noUppercase={true}
				/>
				<div>{dogButton}</div>
			</div>
		);

		return normalizedResult;
	}

	function genericNormalizeGrandineResult(result, project) {
		const img = require('../images/' + GRA_LOGOS[project]);

		// Funzione di supporto per trovare la chiave disponibile nel risultato
		const getFirstAvailableKey = (obj, keys) => {
			for (const key of keys) {
				if (obj[key]) {
					return key;
				}
			}
			return null;
		};

		const resultData = result.getUserdata();

		// Prova a trovare il valore del 'name' in base alle chiavi possibili
		const name = getFirstAvailableKey(resultData, [
			'DENOMINAZIONE',
			'FILIALE',
			'Denominazione'
		]);

		// Prova a trovare il valore del 'phone' in base alle chiavi possibili
		const phone = getFirstAvailableKey(resultData, [
			'TELEFONO',
			'TELFISSO',
			'NUMERO_VERDE'
		]);

		// Prova a trovare il valore del 'fax' (se esiste)
		const fax = getFirstAvailableKey(resultData, ['FAX']);

		// Prova a trovare il valore dell'email (se esiste)
		const email = getFirstAvailableKey(resultData, ['EMAIL', 'Email']);

		// Prova a trovare il sito web (se esiste)
		const site = getFirstAvailableKey(resultData, ['SITOWEB', 'Sito web']);

		// Imposta la CTA (può variare tra "Vai al sito" e "Calcola percorso")
		const cta = site ? 'Vai al sito' : 'Calcola percorso';

		// Chiamata alla funzione base per normalizzare i risultati
		const normalizedResult = normalizeGeoCMSBaseResult(
			project,
			result,
			name,
			cta,
			phone,
			fax,
			email,
			site,
			null, // Facebook non è presente in nessuno degli esempi forniti
			<Brand img={img} />
		);

		// Fallback: se il nome non è presente si recupera dalla descrizione
        if (!normalizedResult.name) {
            normalizedResult.name = result.description || (result.data ? result.data.D : 'Centro Convenzionato');
        }

		// Crea il pulsante condizionale in base alla presenza del sito web
		const button = site ? (
			<Button
				variant="special"
				className={infoWindowStyles.button}
				text={normalizedResult.cta}
				link={normalizedResult.site}
			/>
		) : (
			<Button
				variant="special"
				className={clsx(infoWindowStyles.button, 'cta')}
				text={normalizedResult.cta}
			/>
		);

		// Imposta la quarta colonna e il footer come il pulsante creato
		normalizedResult.fourthColumnList = button;
		normalizedResult.infoWindowFooter = button;

		return normalizedResult;
	}

	function normalizeCsvResult(result) {
		if (result.data) {
			result.data.U['DENOMINAZIONE STRUTTURA'] = result.description;
		}

		const data = result.getUserdata();
		const type = csvTypeConverter(data['TIPO STRUTTURA']);
		const detail = result.getUserdataGroup('SPEC');
		const hasDetail = detail && detail.length;

		const normalizedResult = normalizeGeoCMSBaseResult(
			PROJECT_CSV,
			result,
			'DENOMINAZIONE STRUTTURA',
			hasDetail ? 'Vedi scheda completa' : 'Calcola percorso',
			'TELEFONO',
			null,
			null,
			null,
			null,
			<Brand
				variant={type.class}
				content={<Icon icon="icon/salute_white.png" size="x-large" />}
			/>,
			hasDetail ? detail : null
		);

		normalizedResult.pin = PIN_PROP.IMGs[type.class];

		const csvButton = (
			<Button
				variant="special"
				className={clsx(infoWindowStyles.button, 'cta')}
				text={normalizedResult.cta}
			/>
		);

		normalizedResult.fourthColumnList = csvButton;

		normalizedResult.infoWindowFooter = csvButton;

		return normalizedResult;
	}

	return [normalizeGeoCMSResult, normalizeGeoCMSResults, normalizeBaseResult];
}

function getTimeTableProps(data, isCarrozzeria) {
	const daysVal = ['lun', 'mar', 'mer', 'gio', 'ven', 'sab', 'dom'];

	let timeTableProps = [];

	let validator = false;

	for (let i = 0; i < daysVal.length; i++) {
		const dayVal = daysVal[i];
		let morningOpen = data['HHMM_' + dayVal + '_apertura_mat'];
		let morningClose = data['HHMM_' + dayVal + '_chiusura_mat'];
		let afternoonOpen = data['HHMM_' + dayVal + '_apertura_pom'];
		let afternoonClose = data['HHMM_' + dayVal + '_chiusura_pom'];

		if (isCarrozzeria) {
			if (i === 5) {
				morningOpen = data['Hsab'];
				afternoonOpen = '';
			} else if (i === 6) {
				morningOpen = '';
				afternoonOpen = '';
			} else {
				morningOpen = data['Hmatt'];
				afternoonOpen = data['Hpom'];
			}
		}

		timeTableProps[i] = {
			morningOpen: getFormattedHour(morningOpen),
			morningClose: getFormattedHour(morningClose),
			afternoonOpen: getFormattedHour(afternoonOpen),
			afternoonClose: getFormattedHour(afternoonClose)
		};

		if (morningOpen && afternoonOpen) {
			validator = true;
		}
	}

	return validator ? timeTableProps : null;
}

function getFormattedHour(hour) {
	if (hour && hour.length === 4) {
		return hour.match(/.{1,2}/g).join(':');
	}
	return hour;
}

function getLength(list) {
	return Array.isArray(list) ? list.length : list.size();
}

function getListElementAt(list, n) {
	return Array.isArray(list) ? list[n] : list.getAt(n);
}
